import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Box,
  Button,
  Card,
  CircularProgress,
  Step,
  StepLabel,
  Stepper,
  Typography,
  Alert,
} from "@mui/material";

import { Check } from "@mui/icons-material";
import { AlertCircle, ClipboardPen } from "lucide-react";

import { useForm } from "react-hook-form";
import { useMediaQuery, useTheme } from "@mui/material";

import { EmiWrapperComponent } from "../emiCalculator";

import { BackButton, NextButton } from "../../components/buttons";

import DriverDetailsForm from "./forms/DriverDetailsForm";
import VehicleFinancierDetailsForm from "./forms/VehicleFinancierDetailsForm";

import DocumentSection from "./financierSections/DocumentSection";

import {
  DOCUMENTS_BY_FINANCIER,
  FINANCIER_TYPES,
} from "./constants/documents.config";

import { isRequiredDocument } from "./utils/documentHelpper";

import { mapDocumentsToUploadedFiles } from "./utils/mapDocumentsToUploadedFiles";

import {
  driverDetailsSchema,
  getVehicleFinancierSchema,
} from "./forms/formSchema";

import { uploadAllPreOnboardingFiles } from "../../utils/uploadPreOnboardingFiles";

import {
  getPreOnboardingDriverByPnr,
  updatePreOnboardingDriver,
} from "../../api/modules/preOnboarding";
import { useNavigate, useParams } from "react-router-dom";
import FileViewDialog from "../onboarding/details/FileViewDialog";
import SuccessDialog from "./SuccessDialog";
import paths from "../../routes/paths";
import FullScreenLoader from "../../components/fullScreenLoader";

const DRIVER_DETAIL_FIELDS = [
  "driverName",
  "mobileNumber",
  "alternateMobileNumber",
  "residentialAddress",
  "age",
  "vehicleFinanced",
  "otherInformation",
];

const VEHICLE_FINANCIER_FIELDS = [
  "vehicleFinancierName",
  "vehicleFinancierAddress",
  "vehicleFinancierPhone",
];

const isBrowserFile = (file) => {
  return typeof File !== "undefined" && file instanceof File;
};

const getNewFilesOnly = (uploadedFiles = {}) => {
  const newFiles = {};

  Object.entries(uploadedFiles).forEach(([documentId, files]) => {
    if (!files) return;

    if (files.front || files.back) {
      const documentFiles = {};

      if (isBrowserFile(files.front)) {
        documentFiles.front = files.front;
      }

      if (isBrowserFile(files.back)) {
        documentFiles.back = files.back;
      }

      if (documentFiles.front || documentFiles.back) {
        newFiles[documentId] = documentFiles;
      }

      return;
    }

    if (Array.isArray(files.multiple)) {
      const filesToUpload = files.multiple.filter(isBrowserFile);

      if (filesToUpload.length > 0) {
        newFiles[documentId] = {
          multiple: filesToUpload,
        };
      }

      return;
    }

    if (files.single && isBrowserFile(files.single)) {
      newFiles[documentId] = {
        single: files.single,
      };
    }
  });

  return newFiles;
};

const buildDocumentsPayload = (
  uploadedFiles = {},
  uploadedDocuments = {},
  originalDocuments = {},
) => {
  const payload = {
    ...originalDocuments,
  };

  Object.entries(uploadedFiles).forEach(([documentId, files]) => {
    if (!files) return;

    const original = originalDocuments[documentId] || {};

    const uploaded = uploadedDocuments[documentId] || {};

    if (files.front || files.back) {
      payload[documentId] = {
        ...original,
        frontUrl:
          uploaded.frontUrl || files.front?.url || original.frontUrl || null,
        backUrl:
          uploaded.backUrl || files.back?.url || original.backUrl || null,
      };

      return;
    }

    if (Array.isArray(files.multiple)) {
      const existingUrls = files.multiple
        .filter((file) => !isBrowserFile(file) && file?.url)
        .map((file) => file.url);

      const newlyUploadedUrls = uploaded.urls || [];

      payload[documentId] = {
        ...original,
        urls: [...existingUrls, ...newlyUploadedUrls],
      };

      return;
    }

    if (files.single) {
      payload[documentId] = {
        ...original,
        url: uploaded.url || files.single?.url || original.url || null,
      };
    }
  });

  return payload;
};

// Builds a comparable snapshot of the CURRENT uploadedFiles state:
// existing files are represented by their url, newly-picked browser
// Files are represented by the "NEW" marker (their content isn't
// comparable, but their presence is what matters for diffing).
const getUploadedFilesSnapshot = (uploadedFiles = {}) => {
  const snapshot = {};

  Object.entries(uploadedFiles).forEach(([documentId, files]) => {
    if (!files) return;

    if (files.front || files.back) {
      snapshot[documentId] = {
        front: isBrowserFile(files.front) ? "NEW" : files.front?.url || null,
        back: isBrowserFile(files.back) ? "NEW" : files.back?.url || null,
      };

      return;
    }

    if (Array.isArray(files.multiple)) {
      snapshot[documentId] = {
        multiple: files.multiple.map((file) =>
          isBrowserFile(file) ? "NEW" : file?.url || null,
        ),
      };

      return;
    }

    if (files.single !== undefined) {
      snapshot[documentId] = {
        single: isBrowserFile(files.single) ? "NEW" : files.single?.url || null,
      };
    }
  });

  return snapshot;
};

// Builds the same shape of snapshot from the ORIGINAL server-side
// documents object, so the two can be diffed directly.
const getBaselineDocumentsSnapshot = (documents = {}) => {
  const snapshot = {};

  Object.entries(documents).forEach(([documentId, doc]) => {
    if (!doc) return;

    if (doc.frontUrl !== undefined || doc.backUrl !== undefined) {
      snapshot[documentId] = {
        front: doc.frontUrl || null,
        back: doc.backUrl || null,
      };

      return;
    }

    if (Array.isArray(doc.urls)) {
      snapshot[documentId] = {
        multiple: doc.urls,
      };

      return;
    }

    if (doc.url !== undefined) {
      snapshot[documentId] = {
        single: doc.url || null,
      };
    }
  });

  return snapshot;
};

// True if anything about the documents differs from baseline: new
// files picked, existing files removed, or a "multiple" list
// reordered/shortened. Covers additions AND removals.
const documentsChanged = (uploadedFiles, baselineDocuments) => {
  const current = getUploadedFilesSnapshot(uploadedFiles);
  const baseline = getBaselineDocumentsSnapshot(baselineDocuments);

  const allIds = new Set([...Object.keys(current), ...Object.keys(baseline)]);

  for (const documentId of allIds) {
    const currentValue = JSON.stringify(current[documentId] || null);
    const baselineValue = JSON.stringify(baseline[documentId] || null);

    if (currentValue !== baselineValue) {
      return true;
    }
  }

  return false;
};

const fieldsDiffer = (current, baseline, field) =>
  (current[field] ?? "") !== (baseline?.[field] ?? "");

const getChangedFields = (current, baseline, fields) => {
  const changed = {};

  fields.forEach((field) => {
    if (fieldsDiffer(current, baseline, field)) {
      changed[field] = current[field];
    }
  });

  return changed;
};

const UpdatePreOnboarding = memo(() => {
  const theme = useTheme();

  const { prn } = useParams();

  const navigate = useNavigate();

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const {
    register,
    control,
    watch,
    reset,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    mode: "onBlur",
    defaultValues: {
      driverName: "",
      mobileNumber: "",
      alternateMobileNumber: "",
      residentialAddress: "",
      age: "",
      vehicleFinanced: "No",
      vehicleFinancierName: "",
      vehicleFinancierAddress: "",
      vehicleFinancierPhone: "",
      otherInformation: "",
    },
  });
  
  console.log('errors: ', errors);
  
  const formValues = watch();

  const [driverData, setDriverData] = useState(null);

  const [uploadedFiles, setUploadedFiles] = useState({});

  const [documentErrors, setDocumentErrors] = useState({});

  const [activeInnerStep, setActiveInnerStep] = useState(0);

  const [isLoading, setIsLoading] = useState(true);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [loadError, setLoadError] = useState("");

  const [previewDialog, setPreviewDialog] = useState({
    open: false,
    file: null,
    fileName: "",
  });

  const [resultDialog, setResultDialog] = useState({
    open: false,
    success: false,
    prn: "",
    driverName: "",
    message: "",
  });

  // Snapshot of the data as it was originally loaded from the server.
  // Used to diff against current form/document state so we only ever
  // send changed fields, and never call the update API with no changes.
  const baselineRef = useRef(null);

  const loadDriver = useCallback(async () => {
    if (!prn) {
      setLoadError("PRN is required.");
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setLoadError("");

      const response = await getPreOnboardingDriverByPnr(prn);

      const result = response?.data?.data || response?.data || response;

      setDriverData(result);

      const driverDetailsBaseline = {
        driverName: result.driverDetails?.driverName || "",
        mobileNumber: result.driverDetails?.mobileNumber || "",
        alternateMobileNumber:
          result.driverDetails?.alternateMobileNumber || "",
        residentialAddress: result.driverDetails?.residentialAddress || "",
        age: result.driverDetails?.age || "",
        vehicleFinanced: result.driverDetails?.vehicleFinanced || "No",
        otherInformation: result.driverDetails?.otherInformation || "",
      };

      const vehicleFinancierBaseline = {
        vehicleFinancierName:
          result.vehicleFinancierDetails?.vehicleFinancierName || "",
        vehicleFinancierAddress:
          result.vehicleFinancierDetails?.vehicleFinancierAddress || "",
        vehicleFinancierPhone:
          result.vehicleFinancierDetails?.vehicleFinancierPhone || "",
      };

      reset({
        ...driverDetailsBaseline,
        ...vehicleFinancierBaseline,
      });

      const initialUploadedFiles = mapDocumentsToUploadedFiles(
        result.documents || {},
      );

      setUploadedFiles(initialUploadedFiles);

      baselineRef.current = {
        driverDetails: driverDetailsBaseline,
        vehicleFinancierDetails: vehicleFinancierBaseline,
        documents: result.documents || {},
      };
    } catch (error) {
      console.error("Failed to load pre-onboarding:", error);

      setLoadError(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to load pre-onboarding details.",
      );
    } finally {
      setIsLoading(false);
    }
  }, [prn, reset]);

  useEffect(() => {
    if (!prn) return;

    loadDriver();
  }, [prn]);

  const financierType = driverData?.financierType;

  const FORM_STEPS = useMemo(() => {
    const steps = ["Driver Details"];

    if (formValues.vehicleFinanced === "Yes") {
      steps.push("Vehicle Financier");
    }

    steps.push("Documents");

    return steps;
  }, [formValues.vehicleFinanced]);

  const documentsStep = formValues.vehicleFinanced === "Yes" ? 2 : 1;

  const documentContext = useMemo(
    () => ({
      cibilScore: driverData?.loanCalculationSummary?.cibilScore ?? null,

      vehicleFinanced: formValues.vehicleFinanced,

      ageException: Number(formValues.age) > 55 || Number(formValues.age) < 18,
    }),
    [driverData, formValues.vehicleFinanced, formValues.age],
  );

  // True only once the baseline has loaded AND something has actually
  // changed relative to it (driver details, financier details, or new
  // document uploads). Drives both the button's disabled state and
  // whether handleUpdate is allowed to call the API at all.
  const hasChanges = useMemo(() => {
    const baseline = baselineRef.current;

    if (!baseline) return false;

    // const driverChanged =
    //   Object.keys(
    //     getChangedFields(
    //       formValues,
    //       baseline.driverDetails,
    //       DRIVER_DETAIL_FIELDS,
    //     ),
    //   ).length > 0;

    // if (driverChanged) return true;

    // if (formValues.vehicleFinanced === "Yes") {
    //   const financierChanged =
    //     Object.keys(
    //       getChangedFields(
    //         formValues,
    //         baseline.vehicleFinancierDetails,
    //         VEHICLE_FINANCIER_FIELDS,
    //       ),
    //     ).length > 0;

    //   if (financierChanged) return true;
    // }

    if (documentsChanged(uploadedFiles, baseline.documents)) return true;

    return false;
  }, [formValues, uploadedFiles]);

  const handleDocumentUpload = useCallback((documentId, files, side = null) => {
    setUploadedFiles((previous) => {
      const current = previous[documentId] || {};

      if (side === "front" || side === "back") {
        return {
          ...previous,
          [documentId]: {
            ...current,
            [side]: files[0],
          },
        };
      }

      if (side === "multiple") {
        return {
          ...previous,
          [documentId]: {
            ...current,
            multiple: [...(current.multiple || []), ...files],
          },
        };
      }

      return {
        ...previous,
        [documentId]: {
          ...current,
          single: files[0],
        },
      };
    });

    setDocumentErrors((previous) => {
      const updated = {
        ...previous,
      };

      delete updated[documentId];

      return updated;
    });
  }, []);

  const validateDriver = useCallback(async () => {
    try {
      clearErrors();

      await driverDetailsSchema.validate(formValues, {
        abortEarly: false,
      });

      return true;
    } catch (validationError) {
      validationError.inner?.forEach((error) => {
        if (error.path) {
          setError(error.path, {
            type: "manual",
            message: error.message,
          });
        }
      });

      return false;
    }
  }, [formValues, clearErrors, setError]);

  const validateFinancier = useCallback(async () => {
    try {
      clearErrors();

      await getVehicleFinancierSchema(formValues.vehicleFinanced).validate(
        formValues,
        {
          abortEarly: false,
        },
      );

      return true;
    } catch (validationError) {
      validationError.inner?.forEach((error) => {
        if (error.path) {
          setError(error.path, {
            type: "manual",
            message: error.message,
          });
        }
      });

      return false;
    }
  }, [formValues, clearErrors, setError]);

  const validateDocuments = useCallback(() => {
    const sections = DOCUMENTS_BY_FINANCIER[financierType] || [];

    const allDocuments = sections.flatMap((section) => section.documents || []);

    const requiredDocuments = allDocuments.filter((doc) =>
      isRequiredDocument(doc, documentContext),
    );

    const newErrors = {};

    requiredDocuments.forEach((doc) => {
      const uploaded = uploadedFiles[doc.id];

      if (!uploaded) {
        newErrors[doc.id] = `${doc.name} is required`;
        return;
      }

      if (doc.uploadType === "both") {
        if (!uploaded.front || !uploaded.back) {
          newErrors[doc.id] = `Both front and back of ${doc.name} are required`;
        }

        return;
      }

      if (doc.uploadType === "multiple") {
        if (!uploaded.multiple || uploaded.multiple.length === 0) {
          newErrors[doc.id] = `At least one file for ${doc.name} is required`;
        }

        return;
      }

      if (!uploaded.single) {
        newErrors[doc.id] = `${doc.name} is required`;
      }
    });

    setDocumentErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  }, [financierType, documentContext, uploadedFiles]);

  const handleUpdate = useCallback(async () => {
    if (isSubmitting || !driverData) {
      return;
    }

    // Always re-validate before hitting the API, regardless of which
    // step the user is currently on (the header button can fire from any step).
    // const driverValid = await validateDriver();

    // if (!driverValid) {
    //   setActiveInnerStep(0);
    //   return;
    // }

    // if (formValues.vehicleFinanced === "Yes") {
    //   const financierValid = await validateFinancier();

    //   if (!financierValid) {
    //     setActiveInnerStep(1);
    //     return;
    //   }
    // }

    const documentsValid = validateDocuments();

    if (!documentsValid) {
      setActiveInnerStep(documentsStep);
      return;
    }

    // Guard: never call the update API unless something actually changed.
    if (!hasChanges) {
      setResultDialog({
        open: true,
        success: true,
        prn: driverData.prn || prn,
        driverName:
          formValues.driverName || driverData.driverDetails?.driverName || "",
        message: "No changes were made, so there is nothing to update.",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      // ...rest unchanged

      const baseline = baselineRef.current || {
        driverDetails: {},
        vehicleFinancierDetails: {},
        documents: {},
      };

      const newFiles = getNewFilesOnly(uploadedFiles);

      let uploadedDocuments = {};

      if (Object.keys(newFiles).length > 0) {
        uploadedDocuments = await uploadAllPreOnboardingFiles(
          newFiles,
          formValues.mobileNumber,
        );
      }

      // Build a minimal payload containing only fields that changed,
      // instead of spreading the entire driverData object.
      const payload = {
        prn: driverData.prn || prn,
        financierType: driverData.financierType,
      };

      const changedDriverFields = getChangedFields(
        formValues,
        baseline.driverDetails,
        DRIVER_DETAIL_FIELDS,
      );

      if (Object.keys(changedDriverFields).length > 0) {
        payload.driverDetails = {
          ...(driverData.driverDetails || {}),
          ...changedDriverFields,
        };
      }

      if (formValues.vehicleFinanced === "Yes") {
        const changedFinancierFields = getChangedFields(
          formValues,
          baseline.vehicleFinancierDetails,
          VEHICLE_FINANCIER_FIELDS,
        );

        if (Object.keys(changedFinancierFields).length > 0) {
          payload.vehicleFinancierDetails = {
            ...(driverData.vehicleFinancierDetails || {}),
            ...changedFinancierFields,
          };
        }
      } else if (baseline.driverDetails.vehicleFinanced === "Yes") {
        // Switched from financed to not-financed: clear it out.
        payload.vehicleFinancierDetails = null;
      }

      const docsChanged = documentsChanged(uploadedFiles, baseline.documents);

      if (docsChanged) {
        payload.documents = buildDocumentsPayload(
          uploadedFiles,
          uploadedDocuments,
          driverData.documents || {},
        );
      }

      await updatePreOnboardingDriver(driverData.id, payload);

      // Refresh the baseline so a second update in the same session
      // (without reloading the page) diffs against the newly saved state.
      baselineRef.current = {
        driverDetails: {
          ...baseline.driverDetails,
          ...changedDriverFields,
        },
        vehicleFinancierDetails:
          payload.vehicleFinancierDetails === null
            ? {
                vehicleFinancierName: "",
                vehicleFinancierAddress: "",
                vehicleFinancierPhone: "",
              }
            : {
                ...baseline.vehicleFinancierDetails,
                ...(formValues.vehicleFinanced === "Yes"
                  ? getChangedFields(
                      formValues,
                      baseline.vehicleFinancierDetails,
                      VEHICLE_FINANCIER_FIELDS,
                    )
                  : {}),
              },
        documents: payload.documents || baseline.documents,
      };

      setResultDialog({
        open: true,
        success: true,
        prn: driverData.prn || prn,
        driverName:
          formValues.driverName || driverData.driverDetails?.driverName || "",
        message: "Your pre-onboarding form has been updated successfully.",
      });
    } catch (error) {
      console.error("Pre-onboarding update failed:", error);

      setResultDialog({
        open: true,
        success: false,
        prn: driverData?.prn || prn,
        driverName:
          formValues.driverName || driverData?.driverDetails?.driverName || "",
        message:
          error?.response?.data?.message ||
          error?.message ||
          "We were unable to update the pre-onboarding form. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isSubmitting,
    driverData,
    hasChanges,
    uploadedFiles,
    formValues,
    prn,
    validateDriver,
    validateFinancier,
    validateDocuments,
    documentsStep,
  ]);

  const handleNext = useCallback(async () => {
    if (isSubmitting) return;

    if (activeInnerStep === 0) {
      const valid = await validateDriver();

      if (!valid) return;

      setActiveInnerStep(1);

      return;
    }

    if (formValues.vehicleFinanced === "Yes" && activeInnerStep === 1) {
      const valid = await validateFinancier();

      if (!valid) return;

      setActiveInnerStep(2);

      return;
    }

    if (activeInnerStep === documentsStep) {
      const valid = validateDocuments();

      if (!valid) return;

      await handleUpdate();
    }
  }, [
    isSubmitting,
    activeInnerStep,
    documentsStep,
    formValues.vehicleFinanced,
    validateDriver,
    validateFinancier,
    validateDocuments,
    handleUpdate,
  ]);

  const handleBack = useCallback(() => {
    if (isSubmitting) return;

    if (activeInnerStep === 0) {
      return;
    }

    setActiveInnerStep((previous) => previous - 1);
  }, [activeInnerStep, isSubmitting]);

  const handlePreview = useCallback((file, fileName) => {
    if (!file) return;

    setPreviewDialog({
      open: true,
      file,
      fileName: fileName || "document",
    });
  }, []);

  const handleClosePreview = useCallback(() => {
    setPreviewDialog({
      open: false,
      file: null,
      fileName: "",
    });
  }, []);

  const handleCloseResult = useCallback(() => {
    setResultDialog({
      open: false,
      success: false,
      prn: "",
      driverName: "",
      message: "",
    });
    navigate(paths.DRIVER_PRE_ONBARDING);
  }, []);

  const handleDocumentRemove = useCallback((documentId, side, index) => {
    setUploadedFiles((previous) => {
      const current = previous[documentId];

      if (!current) {
        return previous;
      }

      if (side === "multiple") {
        const multiple = Array.isArray(current.multiple)
          ? [...current.multiple]
          : [];

        if (index == null) {
          return previous;
        }

        multiple.splice(index, 1);

        return {
          ...previous,
          [documentId]: {
            ...current,
            multiple,
          },
        };
      }

      const { [side]: _removed, ...rest } = current;

      return {
        ...previous,
        [documentId]: rest,
      };
    });
  }, []);

  if (isLoading) {
    return (
      <Box
        sx={{
          minHeight: 300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />

        <Typography
          sx={{
            color: "#6b7280",
          }}
        >
          Loading pre-onboarding details...
        </Typography>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Alert severity="error" icon={<AlertCircle size={20} />}>
        {loadError}
      </Alert>
    );
  }

  if (
    !driverData ||
    !financierType ||
    !Object.values(FINANCIER_TYPES).includes(financierType)
  ) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: "center",
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: "#dc2626",
          }}
        >
          Invalid pre-onboarding data.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <EmiWrapperComponent
        title="Update Documents"
        subTitle="Update The Required Information"
        icon={<ClipboardPen size={50} />}
        actions={[
          <Button
            key="update"
            onClick={handleUpdate}
            variant="contained"
            size="large"
            startIcon={<Check />}
            disabled={isSubmitting || !hasChanges}
            sx={{
              bgcolor: "#059669",

              "&:hover": {
                bgcolor: "#047857",
              },

              "&:disabled": {
                bgcolor: "#94a3b8",
              },

              order: {
                xs: 2,
                sm: 1,
              },
            }}
          >
            {isSubmitting ? "Updating..." : "Update"}
          </Button>,
        ]}
      >
        <Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 2,
              flexWrap: "wrap",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: "#6b7280",
              }}
            >
              Financier: <strong>{financierType}</strong>
            </Typography>

            <Typography
              variant="body2"
              sx={{
                color: "#6b7280",
              }}
            >
              PRN: <strong>{driverData.prn || prn}</strong>
            </Typography>
          </Box>

          <Card
            sx={{
              mb: 4,
            }}
          >
            <Box
              sx={{
                p: {
                  xs: 2,
                  sm: 3,
                },
              }}
            >
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    mb: 3,
                    color: "#1f2937",
                  }}
                >
                  Update Documents
                </Typography>

                <DocumentSection
                  financierType={financierType}
                  context={documentContext}
                  uploadedFiles={uploadedFiles}
                  onDocumentUpload={handleDocumentUpload}
                  onDocumentRemove={handleDocumentRemove}
                  documentErrors={documentErrors}
                  onPreview={handlePreview}
                />
              </Box>
            </Box>
          </Card>
        </Box>
      </EmiWrapperComponent>

      <FileViewDialog
        open={previewDialog.open}
        onClose={handleClosePreview}
        file={previewDialog.file}
        fileName={previewDialog.fileName}
      />

      {isSubmitting && (
        <FullScreenLoader message="Updating files and submitting..." />
      )}

      <SuccessDialog
        open={resultDialog.open}
        onClose={handleCloseResult}
        prn={resultDialog.prn}
        driverName={resultDialog.driverName}
        success={resultDialog.success}
        message={resultDialog.message}
      />
    </>
  );
});

export default UpdatePreOnboarding;
